import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

type EditableCellProps<T> = {
  value: T;
  onCommit: (next: T) => void;
  parse: (text: string) => T | null;
  render: (value: T) => ReactNode;
  align?: 'left' | 'right';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
};

/**
 * Click-to-edit cell used by the budget "Budgeted" column and the register's
 * notes/payee/category/amount fields. Enter or blur commits, Escape cancels —
 * the same contract as upstream's `Field` + `InputField`.
 */
export function EditableCell<T>({
  value,
  onCommit,
  parse,
  render,
  align = 'left',
  placeholder,
  disabled,
  className,
  ...rest
}: EditableCellProps<T>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function begin() {
    if (disabled) return;
    setDraft(typeof value === 'string' ? value : '');
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const next = parse(draft);
    if (next !== null && next !== value) onCommit(next);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') setEditing(false);
        }}
        className={clsx(
          'w-full rounded-none border border-table-border-selected bg-table-background px-1 py-0.5 text-sm outline-none',
          align === 'right' && 'text-right tnum',
          className,
        )}
        data-testid={rest['data-testid'] ? `${rest['data-testid']}-input` : undefined}
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={begin}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          begin();
        }
      }}
      className={clsx(
        'min-h-[26px] w-full rounded-sm px-1 py-0.5 text-sm outline-none',
        'hover:bg-table-row-background-hover focus-visible:ring-2 focus-visible:ring-table-border-selected',
        align === 'right' && 'text-right tnum',
        disabled && 'cursor-default',
        !disabled && 'cursor-text',
        className,
      )}
      data-testid={rest['data-testid']}
    >
      {render(value) || (
        <span className="text-form-input-text-placeholder">{placeholder}</span>
      )}
    </div>
  );
}
